import { useEffect, useMemo, useState } from 'preact/hooks'
import { lowerSafe } from '@/utils/strings'
import { getAssetTypeRank } from '@/components/webs/constants/assetMeta'
import type { WebAssetListItemDto } from '@/api/asset'

export interface AssetSearchFilter {
  searchQuery: string
  setSearchQuery: (value: string) => void
  typeFilter: string
  setTypeFilter: (value: string) => void
  /** `assets` narrowed by the name search and the type filter. */
  filteredAssets: WebAssetListItemDto[]
  /** Asset types present in the list, in the fixed display order — the Type dropdown options. */
  availableTypes: string[]
}

/**
 * ORB-457: client-side search (by name, case-insensitive "contains") + type filter over an
 * already-loaded web asset list. The per-web list is small and fully in memory, so there is no
 * server round-trip. If the selected type stops being present (e.g. the last asset of that type
 * was deleted), the filter falls back to "all" so the grid can't get stuck on an option that no
 * longer exists.
 */
export function useAssetSearchFilter(assets: WebAssetListItemDto[]): AssetSearchFilter {
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')

  // Types present in this web, in the fixed display order — so we never offer a type that
  // would match nothing.
  const availableTypes = useMemo(() => {
    const present = Array.from(new Set(assets.map((a) => a.Type)))
    return present.sort((a, b) => getAssetTypeRank(a) - getAssetTypeRank(b) || a.localeCompare(b))
  }, [assets])

  // Reset a now-absent type filter so a delete/refresh can't leave the grid filtered to nothing.
  useEffect(() => {
    if (typeFilter !== 'all' && !availableTypes.includes(typeFilter)) {
      setTypeFilter('all')
    }
  }, [availableTypes, typeFilter])

  const filteredAssets = useMemo(() => {
    const query = lowerSafe(searchQuery).trim()
    return assets.filter((asset) => {
      const matchesType = typeFilter === 'all' || asset.Type === typeFilter
      const matchesSearch = query === '' || lowerSafe(asset.Name).includes(query)
      return matchesType && matchesSearch
    })
  }, [assets, searchQuery, typeFilter])

  return { searchQuery, setSearchQuery, typeFilter, setTypeFilter, filteredAssets, availableTypes }
}
